import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Building, Calendar, CheckCircle, AlertTriangle, Plus, Check, Target, Clock, MessageSquare, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@clareefai/ui';
import { useData } from '../../hooks/useData';

interface WidgetProspect {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  requestedPromoter: string;
  requestedDate: string;
  requestedTime: string;
  submissionDate: string;
  status: 'pending_validation' | 'approved' | 'rejected' | 'assigned' | 'completed';
  widget: string;
  notes?: string;
  assignedPromoters?: string[];
}

interface ProspectDetailByWidgetModalProps {
  prospect: WidgetProspect;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (prospectId: string, updates: Partial<WidgetProspect>) => void;
}

const ProspectDetailByWidgetModal: React.FC<ProspectDetailByWidgetModalProps> = ({
  prospect,
  isOpen,
  onClose,
  onUpdate
}) => {
  const { t, i18n } = useTranslation();
  const { promoters } = useData();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showPromoterSelection, setShowPromoterSelection] = useState(false);
  const [selectedNewPromoters, setSelectedNewPromoters] = useState<string[]>([]);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
        setShowPromoterSelection(false);
        setSelectedNewPromoters([]);
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      if (!isOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handlePromoterToggle = (promoterName: string) => {
    setSelectedNewPromoters(prev => 
      prev.includes(promoterName) 
        ? prev.filter(name => name !== promoterName)
        : [...prev, promoterName]
    );
  };

  const handleAssignPromoters = () => {
    const currentPromoters = prospect.assignedPromoters || [prospect.requestedPromoter];
    const updatedPromoters = [...new Set([...currentPromoters, ...selectedNewPromoters])];
    
    onUpdate(prospect.id, { 
      assignedPromoters: updatedPromoters,
      status: 'assigned'
    });
    
    setShowPromoterSelection(false);
    setSelectedNewPromoters([]);
  };

  const handleApprove = async () => {
    setIsApproving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    onUpdate(prospect.id, { 
      status: 'approved',
      assignedPromoters: [prospect.requestedPromoter]
    });
    
    setIsApproving(false);
  };

  const handleReject = async () => {
    setIsRejecting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    onUpdate(prospect.id, { 
      status: 'rejected'
    });
    
    setIsRejecting(false);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending_validation: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      assigned: 'bg-blue-100 text-blue-800',
      completed: 'bg-purple-100 text-purple-800'
    };

    const labels = {
      pending_validation: t('prospects.widgetStatus.pendingValidation'),
      approved: t('prospects.widgetStatus.approved'),
      rejected: t('prospects.widgetStatus.rejected'),
      assigned: t('prospects.widgetStatus.assigned'),
      completed: t('prospects.widgetStatus.completed')
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  // Get assigned promoters
  const assignedPromoters = promoters.filter(p => 
    (prospect.assignedPromoters || [prospect.requestedPromoter]).includes(p.name)
  );

  // Get available promoters (not yet assigned)
  const availablePromoters = promoters.filter(p => 
    !(prospect.assignedPromoters || [prospect.requestedPromoter]).includes(p.name)
  );

  if (!shouldRender) return null;

  return (
    <>
      <div 
        className={`modal-overlay ${isOpen && !isClosing ? 'open' : ''} ${isClosing ? 'closing' : ''}`} 
        onClick={handleOverlayClick} 
      />
      <div className={`fixed top-0 right-0 h-full w-[800px] max-w-[90vw] bg-white shadow-2xl z-[2000] transform transition-transform duration-300 ease-in-out ${
        isOpen && !isClosing ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <h2 className="text-xl font-semibold">{t('prospects.widget.detailTitle')}</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-gray-50 to-white space-y-6" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
          {/* Prospect Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('prospects.widget.submissionDetails')}</h3>
              {getStatusBadge(prospect.status)}
            </div>
            
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-900">{prospect.name}</h4>
                <p className="text-gray-600 flex items-center mt-1">
                  <Building className="w-4 h-4 mr-1" />
                  {prospect.company}
                </p>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-1" />
                    {t('prospects.widget.submitted')}: {new Date(prospect.submissionDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Target className="w-4 h-4 mr-1" />
                    {prospect.widget}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center text-sm text-gray-600">
                <Mail className="w-4 h-4 mr-2" />
                <a href={`mailto:${prospect.email}`} className="hover:text-green-600">
                  {prospect.email}
                </a>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Phone className="w-4 h-4 mr-2" />
                <a href={`tel:${prospect.phone}`} className="hover:text-green-600">
                  {prospect.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Requested Call Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('prospects.widget.requestedCall')}</h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-600">{t('prospects.widget.requestedPromoter')}</span>
                  <p className="text-gray-900">{prospect.requestedPromoter}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('prospects.widget.widgetSource')}</span>
                  <p className="text-gray-900">{prospect.widget}</p>
                </div>
              </div>

              <div className="mt-4">
                <span className="font-medium text-gray-600 block mb-3">{t('prospects.widget.requestedTimeOptions')}</span>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-300">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                      <span className="text-gray-900">{new Date(prospect.requestedDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')} {t('prospects.at')} {prospect.requestedTime}</span>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{t('prospects.widget.primary')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                      <span className="text-gray-700">February 8, 2024 at 10:30 AM</span>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{t('prospects.widget.alternative')}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
                      <span className="text-gray-700">February 10, 2024 at 2:00 PM</span>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{t('prospects.widget.alternative')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assigned Promoters */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('prospects.widget.assignedPromoters')} ({assignedPromoters.length})
              </h3>
              <button
                onClick={() => setShowPromoterSelection(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>{t('prospects.widget.addPromoter')}</span>
              </button>
            </div>
            
            <div className="space-y-3">
              {assignedPromoters.map((promoter) => (
                <div key={promoter.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <Avatar
                    src={promoter.avatar}
                    name={promoter.name}
                    size="md"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{promoter.name}</h4>
                    <p className="text-sm text-gray-600">{promoter.company}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-xs text-gray-500">{promoter.prospectsReferred} {t('prospects.widget.prospectsSuffix')}</span>
                      <span className="text-xs text-gray-500">{promoter.conversionRate}% {t('prospects.widget.conversionSuffix')}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-green-600">
                      €{promoter.totalRewards.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">{t('prospects.widget.rewards')}</div>
                  </div>
                </div>
              ))}

              {assignedPromoters.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('prospects.widget.noPromotersYet')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Validation Actions */}
          {prospect.status === 'pending_validation' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('prospects.widget.validationActions')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isApproving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('prospects.widget.approving')}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>{t('prospects.widget.approveRequest')}</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleReject}
                  disabled={isRejecting}
                  className="flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRejecting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('prospects.widget.rejecting')}</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5" />
                      <span>{t('prospects.widget.rejectRequest')}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-900">{t('prospects.widget.awaitingValidation')}</h4>
                    <p className="text-sm text-yellow-800 mt-1">
                      {t('prospects.widget.awaitingValidationDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Information */}
          {prospect.status === 'approved' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-900">{t('prospects.widget.requestApproved')}</h4>
                    <p className="text-sm text-green-800 mt-1">
                      {t('prospects.widget.requestApprovedDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {prospect.status === 'assigned' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900">{t('prospects.widgetStatus.assignedToPromoters')}</h4>
                    <p className="text-sm text-blue-800 mt-1">
                      {t('prospects.widget.assignedDesc', { count: assignedPromoters.length })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {prospect.status === 'completed' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-purple-900">{t('prospects.widgetStatus.callCompleted')}</h4>
                    <p className="text-sm text-purple-800 mt-1">
                      {t('prospects.widget.callCompletedDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {prospect.status === 'rejected' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900">{t('prospects.widget.requestRejected')}</h4>
                    <p className="text-sm text-red-800 mt-1">
                      {t('prospects.widget.requestRejectedDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {prospect.notes && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('common.notes')}</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-yellow-800">{prospect.notes}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Promoter Selection Modal */}
      {showPromoterSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2001]">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">{t('prospects.widget.addPromoters')}</h3>
              <button
                onClick={() => {
                  setShowPromoterSelection(false);
                  setSelectedNewPromoters([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {availablePromoters.length === 0 ? (
              <div className="text-center py-8">
                <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">{t('prospects.widget.allAssigned')}</h4>
                <p className="text-gray-600">{t('prospects.widget.allAssignedDesc')}</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-600">
                    {t('prospects.widget.selectAdditional')}
                    {selectedNewPromoters.length > 0 && (
                      <span className="font-medium text-blue-600 ml-1">
                        ({selectedNewPromoters.length} {t('prospects.widget.selected')})
                      </span>
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {availablePromoters.map((promoter) => (
                    <div
                      key={promoter.id}
                      onClick={() => handlePromoterToggle(promoter.name)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedNewPromoters.includes(promoter.name)
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <Avatar
                          src={promoter.avatar}
                          name={promoter.name}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{promoter.name}</h4>
                          <p className="text-sm text-gray-600 truncate">{promoter.company}</p>
                        </div>
                        {selectedNewPromoters.includes(promoter.name) && (
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{promoter.prospectsReferred} {t('prospects.widget.prospectsSuffix')}</span>
                        <span>{promoter.conversionRate}% {t('prospects.widget.conversionSuffix')}</span>
                      </div>

                      <div className="mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          promoter.status === 'available' ? 'bg-green-100 text-green-800' :
                          promoter.status === 'testimonial_done' ? 'bg-purple-100 text-purple-800' :
                          promoter.status === 'review' ? 'bg-blue-100 text-blue-800' :
                          promoter.status === 'testimonial_ready' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {promoter.status === 'available' ? t('promoters.kanbanBoard.status.available') :
                           promoter.status === 'testimonial_done' ? t('promoters.kanbanBoard.status.testimonialDone') :
                           promoter.status === 'review' ? t('promoters.kanbanBoard.status.reviewed') :
                           promoter.status === 'testimonial_ready' ? t('promoters.kanbanBoard.status.testimonialReady') :
                           t('promoters.kanbanBoard.status.invited')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    {selectedNewPromoters.length === 1
                      ? t('prospects.form.promoterSelected', { count: selectedNewPromoters.length })
                      : t('prospects.form.promotersSelected', { count: selectedNewPromoters.length })}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowPromoterSelection(false);
                        setSelectedNewPromoters([]);
                      }}
                      className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleAssignPromoters}
                      disabled={selectedNewPromoters.length === 0}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{t('prospects.widget.assignPromoters')}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProspectDetailByWidgetModal;